import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Button, Modal, Select, Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
// Hooks
import { useGetCampaignCollabRoles } from 'hooks';
// Api
import { UPDATE_CAMPAIGN_COLLAB_ROLE } from 'api/campaignV2/mutations';
// Types
import {
  GetCampaignsV2_getCampaigns_entities,
  GetCampaignsV2_getCampaigns_entities_collaborators,
} from 'api/campaignV2/types/GetCampaignsV2';
import {
  UpdateCampaignCollabRole,
  UpdateCampaignCollabRoleVariables,
} from 'api/campaignV2/types/UpdateCampaignCollabRole';
// UI
import Table from 'ui/Table';
import { errorNotification, successNotification } from 'ui/Notification';
// Components
import CollaborationActions from '../CollaborationActions/CollaborationActions';
import InviteCollaboratorModal from '../InviteCollaboratorModal/InviteCollaboratorModal';

type CollaboratorModalProps = {
  show: boolean;
  handleClose: () => void;
  record: GetCampaignsV2_getCampaigns_entities;
  refetch: () => void;
};

const CollaboratorModal = ({
  show,
  handleClose,
  record,
  refetch,
}: CollaboratorModalProps): JSX.Element => {
  const [inviteModalData, setInviteModalData] = useState<string | null>(null);
  const hideInviteModal = () => setInviteModalData(null);

  const { data: campaignCollabRoles } = useGetCampaignCollabRoles();
  const permissionsOptions =
    campaignCollabRoles?.getPublicCampaignCollabRoles?.map((item) => ({
      label: item.roleId,
      value: item.roleId,
    }));
  const [updateCampaignCollabRole] = useMutation<
    UpdateCampaignCollabRole,
    UpdateCampaignCollabRoleVariables
  >(UPDATE_CAMPAIGN_COLLAB_ROLE);

  const collaborators = record.collaborators || [];
  const campaignId = record.id;
  const handleUpdateRole = async (userId: string, newRoleId: string) => {
    try {
      await updateCampaignCollabRole({
        variables: {
          input: {
            userId,
            newRoleId,
            campaignId,
          },
        },
      });

      successNotification('Collaborator role updated!');
      handleClose();
      refetch();
    } catch (error: any) {
      errorNotification(
        error?.message || 'Something went wrong, please try again later'
      );
    }
  };
  const columns = [
    {
      title: 'Name',
      dataIndex: 'user',
      align: 'center' as const,
      width: 50,
      render: (user: any) => user.firstName + ' ' + user.lastName,
    },
    {
      title: 'Permission',
      dataIndex: 'role',
      align: 'center' as const,
      width: 50,
      render: function RoleWrap(
        id: string,
        record: GetCampaignsV2_getCampaigns_entities_collaborators
      ) {
        const roleId = record.role?.roleId || '';
        const userId = record.user?.id || '';
        if (roleId === 'Owner') {
          return <p>{roleId}</p>;
        }
        return (
          <div>
            <Select
              value={record.role?.roleId || undefined}
              options={permissionsOptions}
              onChange={(value: string) => {
                handleUpdateRole(userId, value);
              }}
            />
          </div>
        );
      },
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      fixed: 'right' as const,
      align: 'center' as const,
      width: 110,
      render: function ActionsWrap(
        id: string,
        record: GetCampaignsV2_getCampaigns_entities_collaborators
      ) {
        if (record.role?.roleId === 'Owner') {
          return null;
        }
        return (
          <CollaborationActions
            record={record}
            campaignId={campaignId}
            onClose={handleClose}
            refetch={refetch}
          />
        );
      },
    },
  ];
  return (
    <>
      <Modal
        title="Collaborators"
        width={1000}
        visible={show}
        onCancel={handleClose}
      >
        <Space direction="vertical" align="start">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setInviteModalData(campaignId)}
          >
            Invite
          </Button>
          <Table columns={columns} data={collaborators} />
        </Space>
      </Modal>
      {inviteModalData && (
        <InviteCollaboratorModal
          campaignId={inviteModalData}
          visible={Boolean(inviteModalData)}
          onClose={hideInviteModal}
        />
      )}
    </>
  );
};

export default CollaboratorModal;
